import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Project } from '../types';
import ProjectDetailsModal from './ProjectDetailsModal';
import { useTheme } from '../contexts/ThemeContext';

interface ProjectsProps {
  onOpenQueryTable?: (project: Project) => void;
  projects: Project[];
  isLoading: boolean;
}

const Projects: React.FC<ProjectsProps> = ({ onOpenQueryTable, projects, isLoading }) => {
  const { effectiveTheme } = useTheme();
  // const [projects, setProjects] = useState<Project[]>([]); // Remove local projects state
  // const [isLoading, setIsLoading] = useState<boolean>(true); // Remove local isLoading state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
    is_private: true
  });

  const handleCreateProject = () => {
    setShowCreateModal(true);
    setFormData({
      name: '',
      description: '',
      tags: '',
      is_private: true
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      notificationService.warning('Project name is required');
      return;
    }

    setIsCreating(true);

    try {
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        is_private: formData.is_private,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };

      await apiService.createProject(projectData);
      
      notificationService.success(`✅ Project "${formData.name}" created successfully`);
      setShowCreateModal(false);
      // loadProjects(); // Call local loadProjects
      // onProjectsChange(); // Notify parent about project change
      
    } catch (error: any) {
      console.error('Error creating project:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create project';
      notificationService.error(`❌ ${errorMessage}`);
    } finally {
      setIsCreating(false);
    }
  };

  // const loadProjects = useCallback(async () => { // Define loadProjects locally
  //   try {
  //     setIsLoading(true);
  //     const data = await apiService.get('/projects/');
  //     setProjects(data.projects);
  //   } catch (error) {
  //     console.error('Error loading projects:', error);
  //     notificationService.show('Failed to load projects', 'error');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, []);

  // useEffect(() => { // useEffect to load projects on mount
  //   loadProjects();
  // }, [loadProjects]);

  const handleOpenProject = async (projectId: number) => {
    try {
      const project = await apiService.getProject(projectId);
      setSelectedProject(project);
      setShowProjectModal(true);
    } catch (error: any) {
      console.error('Error opening project:', error);
      notificationService.error('Failed to open project');
    }
  };

  const handleCloseProjectModal = () => {
    setShowProjectModal(false);
    setSelectedProject(null);
  };

  return (
    <div style={{
      padding: '1.5rem',
      height: 'calc(100vh - 120px)',
      minHeight: '600px',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--surface-color)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexShrink: 0
      }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Your projects</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
            {projects.length} of 20 project limit
          </p>
        </div>
        <button
          onClick={handleCreateProject}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all var(--transition-normal)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--primary-hover)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--primary-color)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <i className="bi bi-plus"></i>
          New Project
        </button>
      </div>

      {/* Projects Grid Container */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        }}>
          {/* New Project Card */}
          <div
            onClick={handleCreateProject}
            style={{
              background: 'var(--bg-color)',
              border: '2px dashed var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              padding: '3rem 2rem',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minHeight: '240px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '20px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-color)';
              e.currentTarget.style.background = 'var(--surface-color)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.background = 'var(--bg-color)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'var(--surface-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <i className="bi bi-plus" style={{
                fontSize: '1.5rem',
                color: 'var(--primary-color)'
              }}></i>
            </div>
            <h6 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>New projectt</h6>
            <small style={{ color: 'var(--text-secondary)' }}>Each project can contain up to 1,000 files</small>
          </div>
          
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  minHeight: '240px',
                  boxShadow: 'var(--shadow-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '20px'
                }}
              >
                <div style={{ marginBottom: '1rem', flex: 1 }}>
                  <Skeleton height={20} width="80%" style={{ marginBottom: '0.5rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <Skeleton height={15} width="90%" count={2} style={{ marginBottom: '0.75rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div style={{
                      background: 'var(--surface-color)',
                      padding: '0.75rem',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)'
                    }}>
                      <Skeleton height={15} width="60%" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                      <Skeleton height={10} width="80%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </div>
                    <div style={{
                      background: 'var(--surface-color)',
                      padding: '0.75rem',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)'
                    }}>
                      <Skeleton height={15} width="60%" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                      <Skeleton height={10} width="80%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '0.75rem',
                  borderTop: '1px solid var(--border-color)',
                  
                }}>
                  <Skeleton height={20} width="40%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <Skeleton height={15} width="20%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                </div>
              </div>
            ))
          ) : (
            /* Project Cards */
            projects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleOpenProject(project.id)}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '240px',
                  boxShadow: 'var(--shadow-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '20px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                  {/* Project Header */}
                  <div style={{ marginBottom: '1rem', flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <h5 style={{
                        color: 'var(--text-primary)',
                        margin: 0,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        flex: 1,
                        marginRight: '0.5rem'
                      }}>
                        {project.name}
                      </h5>
                      <div style={{
                        background: project.is_private
                          ? (effectiveTheme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2')
                          : (effectiveTheme === 'dark' ? 'rgba(16, 185, 129, 0.2)' : '#f0fdf4'),
                        color: project.is_private ? 'var(--danger-color)' : 'var(--success-color)',
                        padding: '0.125rem 0.375rem',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        <i className={`bi ${project.is_private ? 'bi-lock' : 'bi-globe'}`}></i>
                        {project.is_private ? 'Private' : 'Public'}
                      </div>
                    </div>
                    {project.description && (
                      <p style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.85rem',
                        margin: '0 0 0.75rem 0',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {project.description}
                      </p>
                    )}
                    
                    {/* Project Stats */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        background: 'var(--surface-color)',
                        padding: '0.75rem',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <i className="bi bi-file-earmark" style={{ color: 'var(--primary-color)', fontSize: '1rem' }}></i>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.1rem' }}>
                            {project.file_count}
                          </span>
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          Files ({(project.total_size_bytes / (1024 * 1024)).toFixed(1)} MB)
                        </div>
                      </div>
                      
                      <div style={{
                        background: 'var(--surface-color)',
                        padding: '0.75rem',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <i className="bi bi-search" style={{ color: 'var(--success-color)', fontSize: '1rem' }}></i>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.1rem' }}>
                            {project.query_count}
                          </span>
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                          Queries
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid var(--border-color)'
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
                      {project.tags && project.tags.length > 0 ? (
                        project.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              background: 'var(--surface-color)',
                              color: 'var(--text-secondary)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border-color)'
                            }}
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>No tags</span>
                      )}
                      {project.tags && project.tags.length > 2 && (
                        <span style={{
                          background: 'var(--border-color)',
                          color: 'var(--text-secondary)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.75rem'
                        }}>
                          +{project.tags.length - 2}
                        </span>
                      )}
                    </div>
                    <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {new Date(project.updated_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </small>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 1050
          }}
        >
          <div className="modal-dialog">
            <div 
              className="modal-content" 
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div 
                className="modal-header" 
                style={{
                  background: 'var(--bg-color)',
                  borderBottom: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              >
                <h5 className="modal-title" style={{ color: 'var(--text-primary)' }}>
                  <i className="bi bi-folder-plus me-2"></i>
                  Create New Project
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    filter: 'invert(1)',
                    opacity: 0.8
                  }}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ background: 'var(--bg-color)' }}>
                  <div className="mb-3">
                    <label htmlFor="projectName" className="form-label" style={{ color: 'var(--text-primary)' }}>
                      <i className="bi bi-tag me-2"></i>
                      Project Name *
                    </label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="projectName"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      style={{
                        background: 'var(--surface-color)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="projectDescription" className="form-label" style={{ color: 'var(--text-primary)' }}>
                      <i className="bi bi-file-text me-2"></i>
                      Description
                    </label>
                    <textarea 
                      className="form-control" 
                      id="projectDescription"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      style={{
                        background: 'var(--surface-color)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)'
                      }}
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="projectTags" className="form-label" style={{ color: 'var(--text-primary)' }}>
                      <i className="bi bi-tags me-2"></i>
                      Tags
                    </label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="projectTags"
                      name="tags"
                      placeholder="Enter tags separated by commas"
                      value={formData.tags}
                      onChange={handleInputChange}
                      style={{
                        background: 'var(--surface-color)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="projectPrivate"
                      name="is_private"
                      checked={formData.is_private}
                      onChange={handleInputChange}
                      style={{
                        backgroundColor: formData.is_private ? 'var(--primary-color)' : 'var(--surface-color)',
                        borderColor: 'var(--border-color)'
                      }}
                    />
                    <label className="form-check-label" htmlFor="projectPrivate" style={{ color: 'var(--text-primary)' }}>
                      <i className="bi bi-lock me-2"></i>
                      Private project
                    </label>
                  </div>
                </div>
                <div 
                  className="modal-footer" 
                  style={{
                    background: 'var(--bg-color)',
                    borderTop: '1px solid var(--border-color)'
                  }}
                >
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowCreateModal(false)}
                    style={{
                      background: 'var(--surface-color)',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isCreating}
                    style={{
                      background: 'var(--primary-gradient)',
                      border: 'none',
                      color: 'white'
                    }}
                  >
                    {isCreating ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus me-2"></i>
                        Create Project
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Project Details Modal */}
      <ProjectDetailsModal
        project={selectedProject}
        isOpen={showProjectModal}
        onClose={handleCloseProjectModal}
        onOpenQueryTable={onOpenQueryTable}
      />
    </div>
  );
};

export default Projects;
